import Button from '@/components/elements/Button';
import Input from '@/components/elements/Input';
import { useFormValidation } from '@/hooks/useFormValidation';
import { validateEmail, validatePhone } from '@/utils/validators';
import { FaSpinner } from 'react-icons/fa6';
import { useState, type FormEvent } from 'react';
import { MailCheck } from 'lucide-react';
import hubspotService from '@/services/HubspotService';

type FormFields = {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  message: string;
  referred_by?: string;
};

const selectOptions = [
  { value: '', label: 'Select an option', disabled: true },
  { value: 'Existing Client', label: 'Existing Client' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Search Engine', label: 'Search Engine' },
  { value: 'Podcast', label: 'Podcast' },
  { value: 'Other', label: 'Other' },
];

const formSchema = {
  first_name: {
    required: true,
    requiredMessage: 'First name is required',
  },
  last_name: {
    required: true,
    requiredMessage: 'Last name is required',
  },
  email: {
    required: true,
    requiredMessage: 'Email is required',
    validate: validateEmail,
  },
  phone: {
    validate: validatePhone,
  },
  message: {
    required: true,
    requiredMessage: 'Message is required',
  },
};

function ContactForm() {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const { errors, setErrors, isSubmitting, setIsSubmitting, validateForm, resetErrors } =
    useFormValidation<FormFields>(formSchema);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    resetErrors();
    setIsSubmitting(true);

    const formData = new FormData(event.currentTarget);
    const formErrors = validateForm(formData);
    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      try {
        const { VITE_HUBSPOT_FORM_ID, VITE_HUBSPOT_PORTAL_ID } = import.meta.env;

        // If phone value contains only country code then remove it.
        // This is because when only a country code is provided, the phone number is not valid.
        const phoneValue = (formData.get('phone') as string) || '';
        const phoneToSubmit = phoneValue.replace(/^\+\d+$/, '').trim() || '';

        await hubspotService.submitForm(VITE_HUBSPOT_FORM_ID, VITE_HUBSPOT_PORTAL_ID, [
          { name: 'firstname', value: formData.get('first_name') },
          { name: 'lastname', value: formData.get('last_name') },
          { name: 'email', value: formData.get('email') },
          { name: 'phone', value: phoneToSubmit },
          { name: 'message', value: formData.get('message') },
          { name: 'how_did_you_hear_about_us_', value: formData.get('referred_by') || '' },
        ]);
        setIsSubmitted(true);
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('Failed to submit the form.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  }

  if (isSubmitted) {
    return (
      <div className='w-full flex flex-col items-center text-center py-12'>
        <div className='bg-gray-200 flex items-center justify-center mb-4 h-12 w-12 rounded-full'>
          <MailCheck className='w-[24px] h-[24px]' />
        </div>
        <p className='text-lg font-bold mb-2'>Thank You!</p>
        <p className='text-muted leading-tight mb-5 w-[80%]'>
          Your message has been successfully submitted. We&apos;ll get back to you shortly.
        </p>
        <Button
          buttonText='Send Another Message'
          variant='hollow'
          className='w-max px-8'
          onClick={() => setIsSubmitted(false)}
        />
      </div>
    );
  }

  return (
    <form className='w-full flex flex-col gap-4' onSubmit={handleSubmit}>
      <div className='grid grid-cols-2 gap-4'>
        <Input name='first_name' type='text' label='First Name' error={errors.first_name} />
        <Input name='last_name' type='text' label='Last Name' error={errors.last_name} />
      </div>

      <Input name='email' label='Email' error={errors.email} />
      <Input name='phone' type='tel' label='Phone Number' error={errors.phone} />
      <Input type='textarea' name='message' label='Message' error={errors.message} />
      <Input type='select' name='referred_by' label='How did you hear about us?' options={selectOptions} />

      <p className='mt-2 text-sm text-[#A1A1A1] leading-[1.125]'>
        eklipX needs the contact information you provide to us to contact you about our products and services. You may
        unsubscribe from these communications at anytime. For information on how to unsubscribe, as well as our privacy
        practices and commitment to protecting your privacy, see our{' '}
        <a className='text-[#7AD817] underline' href='https://eklipx.io/privacy-policy/'>
          Privacy Policy.
        </a>
      </p>

      <Button type='submit' className='w-[130px] px-8' disabled={isSubmitting}>
        {isSubmitting ? <FaSpinner className='inline animate-spin mb-1' /> : 'Submit'}
      </Button>
    </form>
  );
}

export default ContactForm;
